/**
 * Redux slice helper utilities
 * 
 * These helpers reduce duplication in slice definitions by providing
 * standard patterns for handling async thunk states (pending, fulfilled, rejected).
 */

import { extractErrorMessage } from '../../utils/errorHandling';

/**
 * Creates standard async thunk handlers for loading/error states
 * @param {Object} thunk - Async thunk from createAsyncThunk
 * @param {string} dataKey - Key to store data in state (default: 'data')
 * @returns {Object} Handlers for pending, fulfilled, rejected
 */
export const createAsyncSliceHandlers = (thunk, dataKey = 'data') => {
    return {
        pending: (state) => {
            state.loading = true;
            state.error = null;
        },
        fulfilled: (state, action) => {
            state.loading = false;
            state.error = null;
            state[dataKey] = action.payload;
        },
        rejected: (state, action) => {
            state.loading = false;
            const errorMessage = extractErrorMessage(action.payload) || action.error?.message;
            state.error = errorMessage || `Failed to ${thunk.typePrefix}`;
        },
    };
};

/**
 * Helper to add standard async handlers to builder
 * @param {Object} builder - Redux Toolkit builder from extraReducers
 * @param {Object} thunk - Async thunk from createAsyncThunk
 * @param {string} dataKey - Key to store data in state (default: 'data')
 */
export const addAsyncHandlers = (builder, thunk, dataKey = 'data') => {
    const handlers = createAsyncSliceHandlers(thunk, dataKey);
    builder
        .addCase(thunk.pending, handlers.pending)
        .addCase(thunk.fulfilled, handlers.fulfilled)
        .addCase(thunk.rejected, handlers.rejected);
};

